import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Collapse, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { ISubscriptionPlan } from '../../redux/subscription/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CircularCheckmark from '../CustomSVGIcons/CircularCheckIcon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { SubscriptionFeatureLabel } from './SubscriptionFeatureLabel';
import { isCreatorTrialAvailable } from '../../utils/creatorSubscriptionTrial';

interface PlanCardProps {
    plan: ISubscriptionPlan;
    isSelected?: boolean;
    onSelect?: () => void;
    planIndex?: number;
    compact?: boolean;
}

const formatCurrency = (price: number, symbol: string = '$') => {
    const amount = price / 100;
    return `${symbol}${amount.toLocaleString('en-US', {
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    })}`;
};

const PlanCard: React.FC<PlanCardProps> = ({ plan, isSelected = false, onSelect, planIndex, compact = false }) => {
    const { themeStyle, darkModeEnabled, device } = useThemeContext();
    const [expanded, setExpanded] = useState(false);
    const t = theme[themeStyle];

    const isMobile = device.isMobile;
    const titleSize = compact ? '1.0625rem' : isMobile ? '1.25rem' : '1.5rem';
    const priceSize = compact ? '1.375rem' : isMobile ? '2rem' : '2.25rem';
    const bodySize = compact ? '0.75rem' : '0.875rem';
    const cardPad = compact ? { xs: 1.5, sm: 1.75 } : { xs: 2, sm: 3 };

    const handleExpandClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isMobile) {
            event.stopPropagation();
            setExpanded(!expanded);
        }
    };

    const selectColorForPlan = () => {
        if (darkModeEnabled) return t.deep_grey_lighter;
        let color = '#F6F6FE ';
        switch (planIndex) {
            case 0:
                color = '#F6F6FE ';
                break;
            case 1:
                color = '#FFF9EB';
                break;
            case 2:
                color = '#fef6faff ';
                break;
        }
        return color;
    };

    const isFeatured = Boolean(plan.preferred_plan);
    const cardBgDark = isFeatured ? 'rgba(38, 38, 38, 0.98)' : '#1a1a1a';
    const cardBackground = darkModeEnabled ? cardBgDark : selectColorForPlan();
    const defaultBorderDark = '1px solid rgba(255,255,255,0.05)';
    const featuredBorderDark = `1px solid ${alpha(t.highlight, 0.35)}`;
    const selectedOrFeaturedBorder = `2px solid ${t.highlight}`;
    const borderLight = '1px solid #ccc';
    const resolvedBorder = darkModeEnabled
        ? isSelected
            ? selectedOrFeaturedBorder
            : isFeatured
            ? featuredBorderDark
            : defaultBorderDark
        : isSelected || isFeatured
        ? selectedOrFeaturedBorder
        : borderLight;

    return (
        <Card
            sx={{
                border: resolvedBorder,
                borderRadius: 3,
                boxShadow:
                    isFeatured && darkModeEnabled
                        ? `0 0 40px -10px ${alpha(t.highlight, 0.28)}`
                        : isSelected
                        ? `0px 4px 10px ${t.selectableBorder}`
                        : 'none',
                cursor: 'pointer',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: isMobile ? 'auto' : '100%',
                minHeight: isMobile ? 0 : 0,
                backgroundColor: cardBackground,
                ...(plan.preferred_plan ? { overflow: 'visible' } : { overflowX: 'hidden', overflowY: 'visible' }),
                m: 0,
                pt: cardPad,
                pr: cardPad,
                pb: cardPad,
                pl: cardPad,
                position: 'relative',
                transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                zIndex: isFeatured ? 2 : 1,
                ...(darkModeEnabled && {
                    '&:hover': {
                        backgroundColor: isFeatured ? 'rgba(42, 42, 42, 1)' : 'rgba(38, 38, 38, 1)',
                    },
                }),
                ...(!darkModeEnabled && {
                    '&:hover': {
                        boxShadow: `0px 4px 12px ${t.shadowColor || 'rgba(0,0,0,0.08)'}`,
                    },
                }),
            }}
            onClick={onSelect}>
            {plan.preferred_plan && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: -12, sm: compact ? -11 : -14 },
                        left: '50%',
                        transform: 'translate(-50%, 0)',
                        backgroundColor: t.highlight,
                        color: t.black,
                        px: compact ? 1.25 : 2,
                        py: compact ? 0.35 : 0.5,
                        borderRadius: 999,
                        fontSize: compact ? '8px' : '10px',
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                        boxShadow: '0 4px 14px rgba(250, 175, 63, 0.35)',
                    }}>
                    Most Popular
                </Box>
            )}
            <CardContent
                sx={{
                    p: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: compact ? 1.25 : 2 }}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: t.primary_white,
                            fontSize: titleSize,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.2,
                        }}>
                        {plan.name}
                    </Typography>
                    <Box display="flex" alignItems="center" flexShrink={0} sx={{ ml: 1, gap: 0.75 }}>
                        {isCreatorTrialAvailable(plan.trial) ? (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: compact ? 9 : 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 800,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: t.highlight,
                                    bgcolor: alpha(t.highlight, 0.16),
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 999,
                                    whiteSpace: 'nowrap',
                                }}>
                                {plan.trial?.days ? `${plan.trial.days}-day trial` : 'Free trial'}
                            </Typography>
                        ) : null}
                        <CircularCheckmark filled={isSelected} color={t.highlight} size={compact ? 16 : 20} />
                        {isMobile && (
                            <IconButton
                                onClick={handleExpandClick}
                                aria-expanded={expanded}
                                aria-label="show more"
                                sx={{ ml: 0.5 }}>
                                {expanded ? (
                                    <KeyboardArrowUpIcon sx={{ color: t.highlight }} />
                                ) : (
                                    <KeyboardArrowDownIcon sx={{ color: t.highlight }} />
                                )}
                            </IconButton>
                        )}
                    </Box>
                </Box>
                {plan.converted ? (
                    <>
                        <Box sx={{ mb: compact ? 1.25 : 2 }}>
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontWeight: 800,
                                    color: t.highlight,
                                    fontSize: priceSize,
                                    lineHeight: 1,
                                }}>
                                {plan.formatted_for_display ?? plan.converted_amount}
                            </Typography>
                            <Typography
                                component="span"
                                sx={{
                                    color: t.tag_white,
                                    opacity: 0.65,
                                    fontSize: bodySize,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    ml: 0.5,
                                }}>
                                / {plan.interval}
                            </Typography>
                        </Box>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="primary"
                            sx={{
                                color: t.highlight,
                                fontSize: {
                                    xs: '0.8rem',
                                    sm: '0.8rem',
                                },
                                opacity: 0.9,
                                mb: 1,
                            }}>
                            Approx. {formatCurrency(plan.amount)}
                        </Typography>
                    </>
                ) : (
                    <Box sx={{ mb: compact ? 1.25 : 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                color: t.highlight,
                                fontSize: priceSize,
                                lineHeight: 1,
                            }}>
                            {formatCurrency(plan.amount, plan.toSymbol)}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                color: t.tag_white,
                                opacity: 0.65,
                                fontSize: bodySize,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                ml: 0.5,
                            }}>
                            / {plan.interval}
                        </Typography>
                    </Box>
                )}
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        color: t.tag_white,
                        opacity: darkModeEnabled ? 0.7 : 1,
                        fontSize: bodySize,
                        lineHeight: 1.45,
                        minHeight: compact ? 32 : 40,
                        mb: compact ? 1.25 : 2,
                    }}>
                    {plan.description}
                </Typography>

                {isMobile ? (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ flex: 1, mb: 2 }}>
                            {plan.features.map((feature, index) => (
                                <Box
                                    display="flex"
                                    alignItems="flex-start"
                                    key={index}
                                    gap={compact ? 1 : 1.5}
                                    sx={{ mt: index === 0 ? 0 : compact ? 1 : 1.5 }}>
                                    <CheckIcon
                                        sx={{
                                            fontSize: compact ? 14 : 18,
                                            color: t.highlight,
                                            mt: 0.15,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <SubscriptionFeatureLabel
                                        feature={feature}
                                        color={t.tag_white}
                                        titleFontSize={bodySize}
                                        detailFontSize={compact ? '0.6875rem' : '0.75rem'}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            mb: compact ? 1.25 : 2,
                            minHeight: 0,
                            overflowY: 'auto',
                            pr: 0.5,
                        }}>
                        {plan.features.map((feature, index) => (
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                key={index}
                                gap={compact ? 1 : 1.5}
                                sx={{ mt: index === 0 ? 0 : compact ? 1 : 1.5 }}>
                                <CheckIcon
                                    sx={{
                                        fontSize: compact ? 14 : 18,
                                        color: t.highlight,
                                        mt: 0.15,
                                        flexShrink: 0,
                                    }}
                                />
                                <SubscriptionFeatureLabel
                                    feature={feature}
                                    color={t.tag_white}
                                    titleFontSize={bodySize}
                                    detailFontSize={compact ? '0.6875rem' : '0.75rem'}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default PlanCard;
